import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import StaffComposer from '../components/admin/StaffComposer';
import RichMessageBody, { hasRichBody } from '../components/RichMessageBody';
import MessageAttachment from '../components/MessageAttachment';
import RightDrawer from '../components/RightDrawer';
import { useTableSort } from '../hooks/useTableSort';
import OwnerCell from '../components/OwnerCell';
import StatCard from '../components/StatCard';
import { Icon } from '../utils/icons';
import KpiDelta from '../components/KpiDelta';
import { countBeforeThisMonth } from '../components/kpiMonth';
import PartnerInviteModal from '../components/PartnerInviteModal';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Mirrors STATUS_CONFIG in AdminREReferrals.js. Archived/adversed are NOT here:
// they are booleans on the row, never application_status values, and treating
// them as statuses is exactly the bug that put an archived borrower on the
// referrals list.
const REFERRAL_STATUS = {
  not_started:     { label: 'Not Started',    color: '#6b7280', bg: '#f3f4f6' },
  started:         { label: 'Started',        color: '#6b7280', bg: '#f3f4f6' },
  in_progress:     { label: 'In Progress',    color: '#6b7280', bg: '#f3f4f6' },
  submitted:       { label: 'Submitted',      color: '#3a6ad9', bg: '#eff6ff' },
  pre_qualified:   { label: 'Pre-Qualified',  color: '#3a6ad9', bg: '#eff6ff' },
  in_review:       { label: 'In Review',      color: '#ffffff', bg: '#4F46E5' },
  approved:        { label: 'Approved',       color: '#1a1a1a', bg: '#acf44e' },
  home_shopping:   { label: 'Home Shopping',  color: '#ffffff', bg: '#7C58F5' },
  offer_requested: { label: 'Offer Requested',color: '#1a1a1a', bg: '#FF7A1A' },
  option_period:   { label: 'Under Contract', color: '#1a1a1a', bg: '#FFDD86' },
  closing:         { label: 'Closing',        color: '#1a1a1a', bg: '#14B8A6' },
  closed:          { label: 'Closed \u2713',      color: '#ffffff', bg: '#111827' },
  registered:      { label: 'Registered',     color: '#3a6ad9', bg: '#eff6ff' },
  // A broker submission whose borrower has not registered (the same label the Partner Referrals page uses).
  broker_submitted: { label: 'Broker — awaiting borrower', color: '#92400e', bg: '#fffbeb' },
};

// 🚨 THE TYPE LABEL COMES FROM THE MEMBERS, NOT FROM account_type. That column
// only ever holds 'homebuilder' or 'brokerage', so every mortgage company is
// stored as a "brokerage" — three of them are today. Calling that value "Real
// Estate Brokerage" would tell a reader that Alta Home Lending and Loan Factory
// are real estate firms.
//
// ⚠️ A MIXED OR EMPTY ACCOUNT FALLS BACK TO THE NEUTRAL WORD rather than picking
// one of its members' types. An account with both a realtor and a loan officer is
// a real thing and neither label would be true of it.
const ACCOUNT_KIND = (acct) => {
  if (acct.account_type === 'homebuilder') return 'Homebuilder';
  const types = String(acct.member_types || '').split(',').map((t) => t.trim()).filter(Boolean);
  if (types.length === 1 && types[0] === 'loan_officer') return 'Mortgage Broker';
  if (types.length === 1 && types[0] === 'realtor') return 'Real Estate Brokerage';
  if (types.length === 1 && types[0] === 'homebuilder') return 'Homebuilder';
  return 'Brokerage';
};

// ⚠️ ONE LABEL/VALUE SHAPE FOR THE WHOLE PANEL, so a field added later cannot
// arrive looking different from the ones beside it. `badge` is for a fact ABOUT
// the value — verified vs claimed, payable vs not — which must not be left to the
// reader to infer from the value alone.
const fieldLabel = { fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 };
const fieldInput = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit', background: '#fff' };
const fieldHint = { fontSize: 11, color: '#9ca3af', marginTop: 4, lineHeight: 1.5 };

// ── 🔑 THE FIRM'S LICENCE, FROM THE RECORDED CHECK (Jesse, 2026-11-02) ─────────────────────────────────────────
// The server sends firm_licence from utils/firmLicence — the columns "Record a check" writes and the licence gate
// reads. "Not verified" only when no check has been recorded; otherwise the check's status, registry, number, date.
export function firmLicenceText(l) {
  if (!l) return { value: '—', badge: null };
  const reg = l.registryLabel ? `${l.registryLabel} ` : '';
  if (!l.checked) {
    return { value: l.number ? `${reg}${l.number} · Not verified` : 'Not verified', badge: null };
  }
  const when = l.verifiedAt
    ? new Date(l.verifiedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;
  return {
    value: `${l.status || 'Checked'} · ${reg}${l.number || ''}${when ? ` · verified ${when}` : ''}`,
    badge: l.verified ? 'verified' : 'not active',
  };
}

function Detail({ label, value, badge, testid }) {
  return (
    <div data-testid={testid}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ color: '#111827', marginTop: 2 }}>
        {value}
        {badge && (
          <span style={{
            marginLeft: 6, fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 6,
            background: badge === 'verified' ? '#f0fdf4' : '#fff0f3',
            color: badge === 'verified' ? '#166534' : '#ff082f',
          }}>{badge}</span>
        )}
      </div>
    </div>
  );
}

export default function AdminREAccounts() {
  const navigate = useNavigate();
  const token = localStorage.getItem('doorly_admin_token');
  const headers = { Authorization: `Bearer ${token}` };
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [partners, setPartners] = useState([]);
  const [referrals, setReferrals] = useState([]);
  // 🔑 COUNTED SERVER-SIDE WITH THE PARTNERS PAGE'S OWN PREDICATE. Summing
  // member_active_count across accounts cannot see a partner who has no account,
  // so the two cards could never agree however the per-account rule was tuned.
  const [partnerTotal, setPartnerTotal] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  // The firm's people, resolved server-side, plus the admin edit state.
  const [detail, setDetail] = useState({ broker: null, payee: null, owner: null });
  const [editingFirm, setEditingFirm] = useState(false);
  const [firmForm, setFirmForm] = useState({ company_name: '', entity_license_number: '', account_note: '', payee_contact_partner_id: '' });
  const [firmSaving, setFirmSaving] = useState(false);
  const [firmError, setFirmError] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerTab, setPartnerTab] = useState('details');
  const [partnerMessages, setPartnerMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [partnerConvId, setPartnerConvId] = useState(null);
  const msgEndRef = useRef(null);
  // 🚨 A NEW PARTNER STARTS WITH AN EMPTY THREAD (fp-format 428dee47, ported alone into the following push). The messages
  // and the conversation id were loaded once and never cleared, so opening partner B after partner A showed A's thread
  // under B's name — and a reply went to A's conversation (threads/:id/reply with A's id). Cleared whenever the partner
  // changes.
  const selectedPartnerId = selectedPartner ? selectedPartner.id : null;
  useEffect(() => {
    setPartnerMessages([]);
    setPartnerConvId(null);
    setPartnerTab('details');
  }, [selectedPartnerId]);

  // 🔑 THE ONE STAFF COMPOSER (fp-format, Jesse 2026-11-01) — the same payload and routes as AdminREPartners.
  const sendPartnerReply = async (payload) => {
    if (!selectedPartner) return false;
    try {
      if (partnerConvId) {
        const r = await axios.post(`${API}/api/admin/messages/threads/${partnerConvId}/reply`, payload, { headers });
        setPartnerMessages(prev => [...prev, r.data.message]);
      } else {
        const r = await axios.post(`${API}/api/admin/partners/${selectedPartner.id}/messages`, payload, { headers });
        setPartnerMessages(prev => [...prev, r.data.message]);
        setPartnerConvId(r.data.conversation?.id);
      }
      setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      return true;
    } catch (e) {
      throw new Error(e.response?.data?.error || 'The message was not sent.');
    }
  };

  // ⚠️ EXTRACTED SO A SAVE CAN RE-READ IT. The list was fetched inline in an
  // effect, so an edit that renames a firm had no way to refresh the row behind
  // the panel — the drawer would show the new name over a table still showing
  // the old one.
  const loadAccounts = useCallback(() => {
    if (!token) return;
    fetch(`${API}/api/admin/partners/accounts`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { setAccounts(d.accounts || []); setPartnerTotal(d.partner_total ?? null); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);
  useEffect(loadAccounts, [loadAccounts]);

  // ── filter state: one dimension per control, the Partners page shape ─────
  //
  // ⚠️ COMBINED WITH AND, NOT A SINGLE SELECT OF COMBINATIONS. A lone <select>
  // holding every option makes "unclaimed brokerages" unaskable — the list grows
  // as the PRODUCT of the dimensions rather than their sum, and every new
  // question needs a new entry.
  const [search, setSearch] = useState('');
  const [pill, setPill] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('All');
  const [stateFilter, setStateFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  // 🔑 THE ROSTER, NOT THE OWNERS PRESENT IN THE ROWS. A dropdown built from the
  // data cannot offer a rep who holds nothing yet — and "which accounts does Troy
  // have" is exactly the question you ask about a rep who may have none.
  const [owners, setOwners] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [attentionOpen, setAttentionOpen] = useState(false);

  // 🔑 THE TWO QUESTIONS A REP OPENS THIS PAGE WITH. Shared by the pills, the
  // KPI cards and the Needs Attention table, so a count can never disagree with
  // the list it claims to summarise.
  // ⚠️ entity_license_verified is the RECORDED licence check now (server: utils/firmLicence). Only true clears it:
  // null (the read failed) still counts as a blocker, with its own reason below — never "verified" by default.
  const cannotBePaid = (a) => !a.ach_completed || !a.has_payee || a.entity_license_verified !== true;
  // 🚨 OWNER STATE 2, NOT 3. A row that holds nobody is claimable; no row at all
  // is a question for a human. See components/OwnerCell.jsx.
  const isClaimable = (a) => (a.assigned_row_count || 0) > 0 && !(a.owner_name || '').trim();

  // ⚠️ THE REASONS ARE DERIVED FROM THE SAME PREDICATE THE COUNT USES, not
  // re-listed. A second list would drift the first time a fourth blocker is
  // added, and the card would then say "3" over a table showing two rows.
  const blockReasons = (a) => {
    const r = [];
    if (!a.ach_completed) r.push('No banking on file');
    if (!a.has_payee) r.push('No payee contact');
    if (a.entity_license_verified == null) r.push('Entity license could not be read');
    else if (a.entity_license_verified !== true) {
      r.push(a.entity_license_claimed ? 'Entity license not verified' : 'No entity license on file');
    }
    return r;
  };

  // ── the KPI row ──────────────────────────────────────────────────────────
  //
  // ⚠️ "Partners" HERE MEANS ACTIVE MEMBERS, and the card says so underneath.
  // partner_count deliberately counts only onboarding_status='active' — the
  // endpoint reports member_count, pending_count and archived_count beside it
  // rather than folding them in — so this total will NOT reconcile against a
  // headcount that includes people still in training. The sublabel carries that,
  // because a bare "Partners: 14" next to "Accounts: 19" invites arithmetic that
  // cannot work and nothing on screen would show why.
  //
  // ⚠️ EVERY FIGURE IS DERIVED FROM `accounts`, THE SAME ARRAY THE TABLE RENDERS.
  // That is what makes is_test_account free: the endpoint excludes test accounts
  // unless ?include_test=1, so Doorly Realty is out of the counts and the list by
  // construction. A separately fetched KPI is the one place that would forget.
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/admin/partners/owners`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => setOwners(d.owners || []))
      .catch(() => {});
  }, [token]);

  // ⚠️ DERIVED FROM THE ROWS, because only the rows know which states are
  // actually represented — offering all fifty would be a dropdown of dead ends.
  const stateOptions = useMemo(() => Array.from(new Set(
    accounts.flatMap((a) => String(a.states || '').split(',').map((x) => x.trim()).filter(Boolean))
  )).sort(), [accounts]);

  const kpis = useMemo(() => {
    const rows = accounts;
    const sum = (f) => rows.reduce((n, a) => n + (Number(a[f]) || 0), 0);
    // ⚠️ THE SHARED HELPER, not this page's own month maths. Three pages each
    // computing "before this month" their own way is how the same phrase starts
    // meaning three things.
    const accountsPrev = countBeforeThisMonth(rows);
    const blocked = rows.filter(cannotBePaid);
    return {
      accounts: rows.length,
      accountsPrev,
      // 🚨 NOT partner_count. That is ACTIVE ONLY — it put 9 on this card beside
      // an Accounts card reading 19, a 2x gap against the Partners page that
      // looked like the two cards were swapped. The Partners page counts every
      // partner whose status is not 'archived'; this is that same rule applied to
      // the accounts this page shows, so the two now differ only by SCOPE (test
      // accounts, and partners with no account at all) rather than by counting a
      // different thing.
      // ⚠️ THE SUM IS THE FALLBACK, not the answer. If the count fails the card
      // shows a slightly low number rather than a zero.
      partners: partnerTotal ?? sum('member_active_count'),
      partnersPrev: sum('member_active_count_prev'),
      // 🔑 FILES = referrals + broker submissions, one server count (utils/partnerFileCounts).
      referrals: sum('file_count'),
      referralsPrev: sum('file_count_prev'),
      blocked: blocked.length,
      blockedRows: blocked,
    };
  }, [accounts, partnerTotal]);

  // 🔑 SORTING — the same hook the Partners and Partner Leads tables use, so the
  // three pages in this section behave identically. Jesse asked for all three.
  const SORT_COLUMNS = useMemo(() => ({
    company:    (a) => a.company_name,
    type:       (a) => a.account_type,
    partners:   { get: (a) => a.partner_count, type: 'number' },
    owner:      (a) => a.owner_name,
    state:      (a) => a.states,
    referrals:  { get: (a) => a.file_count, type: 'number' },
    volume:     { get: (a) => a.closed_volume, type: 'number' },
    created:    { get: (a) => a.created_at, type: 'date' },
  }), []);
  const { sortKey, sortDir, toggle: toggleSort, sortRows } = useTableSort(SORT_COLUMNS);

  const unsortedVisible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return accounts.filter((a) => {
      if (q && !String(a.company_name || '').toLowerCase().includes(q)) return false;
      if (pill === 'cannot_be_paid' && !cannotBePaid(a)) return false;
      if (pill === 'unclaimed' && !isClaimable(a)) return false;

      // ⚠️ NO OWNER AND NOT ON RECORD REMAIN SEPARATE OPTIONS among the named
      // reps. Collapsing them invites a rep to claim a partner in the one case
      // where the honest answer is "ask someone" — see components/OwnerCell.jsx.
      if (ownerFilter === 'no_owner' && !isClaimable(a)) return false;
      if (ownerFilter === 'no_record' && (a.assigned_row_count || 0) !== 0) return false;
      if (ownerFilter !== 'All' && ownerFilter !== 'no_owner' && ownerFilter !== 'no_record'
          && (a.owner_name || '').trim() !== ownerFilter) return false;

      if (stateFilter !== 'All'
          && !String(a.states || '').split(',').map((x) => x.trim()).includes(stateFilter)) return false;

      // ⚠️ FILTERED ON THE SAME DERIVATION THE COLUMN RENDERS, so a row can never
      // be excluded by a filter whose label it is displaying.
      if (typeFilter !== 'All' && ACCOUNT_KIND(a) !== typeFilter) return false;
      return true;
    });
  }, [accounts, search, pill, ownerFilter, stateFilter, typeFilter]);
  // ⚠️ SORT AFTER FILTER, never before — the filter decides WHICH rows and the
  // sort decides their order; reversing that sorts rows the user cannot see.
  const visible = useMemo(() => sortRows(unsortedVisible), [unsortedVisible, sortRows]);


  const openAccount = async (acct) => {
    setSelected(acct);
    setLoadingDetail(true);
    setEditingFirm(false); setFirmError(null);
    try {
      const res = await fetch(`${API}/api/admin/partners/accounts/${acct.id}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      setPartners(d.partners || []);
      setReferrals(d.referrals || []);
      // 🔑 THE FIRM'S PEOPLE COME FROM THE SERVER, NOT FROM A SECOND DERIVATION
      // HERE. Who the broker is, and whether the payee contact is that same
      // person, are decided in one place — two readers of account_role would
      // disagree the first time either changed.
      setDetail({ broker: d.broker || null, payee: d.payee_contact || null, owner: d.owner || null,
        firmLicence: d.firm_licence || null, filesLabel: d.files_label || 'Files' });
      // ⚠️ SEEDED FROM THE DETAIL RESPONSE, not from the list row. The list
      // carries a trimmed projection; editing from it would save stale values
      // for fields the list never fetched.
      setFirmForm({
        company_name: (d.account && d.account.company_name) || acct.company_name || '',
        entity_license_number: (d.account && d.account.entity_license_number) || '',
        account_note: (d.account && d.account.account_note) || '',
        payee_contact_partner_id: (d.account && d.account.payee_contact_partner_id) || '',
      });
      if (d.account) setSelected((prev) => ({ ...prev, ...d.account }));
    } catch (_) {}
    finally { setLoadingDetail(false); }
  };

  // 🔑 ?account=<id> OPENS THAT FIRM'S PANEL (Leads panel, 2026-11-02): the firm link on a lead's source card. Once per
  // page load, after the list has arrived; an id not in this staff member's list opens nothing.
  const accountDeepLinkDone = useRef(false);
  useEffect(() => {
    if (accountDeepLinkDone.current || loading) return;
    const want = Number(new URLSearchParams(window.location.search).get('account'));
    if (!want) { accountDeepLinkDone.current = true; return; }
    const hit = accounts.find((a) => Number(a.id) === want);
    accountDeepLinkDone.current = true;
    if (hit) openAccount(hit);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, loading]);

  // ⚠️ THE PANEL RE-READS AFTER A SAVE rather than patching local state. The
  // server recomputes company_name_normalized and clears the license
  // verification stamp; a local merge would show a "verified" badge against a
  // number the save just un-verified.
  const saveFirm = async () => {
    setFirmSaving(true); setFirmError(null);
    try {
      const body = {
        company_name: firmForm.company_name,
        entity_license_number: firmForm.entity_license_number,
        account_note: firmForm.account_note,
        payee_contact_partner_id: firmForm.payee_contact_partner_id === ''
          ? null : Number(firmForm.payee_contact_partner_id),
      };
      const res = await fetch(`${API}/api/admin/partners/accounts/${selected.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) { setFirmError(d.error || 'Could not save.'); return; }
      setEditingFirm(false);
      await openAccount({ ...selected, id: selected.id });
      loadAccounts();
    } catch (e) { setFirmError(e.message); }
    finally { setFirmSaving(false); }
  };

  return (
    <AdminLayout>
      <div className="admin-page-body">
        {/* ⚠️ flexWrap WAS THIS PAGE'S OWN ANSWER to the same problem, and it
            gave a third result again: the action group dropped to its own line
            only once it no longer fitted, so the subtitle still lost most of the
            width first. The shared rule stacks deliberately at the breakpoint. */}
        <div className="admin-page-head">
          <div>
            <h1 style={{ margin: '0 0 4px', fontSize: '22px', fontWeight: '700', color: '#111827' }}>Partner Accounts</h1>
            <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>Brokerages, mortgage companies, and homebuilders linked to DoorlyPro partners</p>
          </div>
          {/* ⚠️ THE SAME MECHANISM AS THE PARTNERS PAGE, NOT A SECOND ONE. This
              posts to POST /admin/partners/invite with kind:'account' — one
              partner_invites row, the account email copy, and the recipient lands
              on /doorlypro/start like every other entry point. */}
          {/* ⚠️ SIZED TO MATCH THE PARTNERS AND LEADS BUTTONS, not independently.
              This was 10px/14px/700 against their 9px/13px/600 — a few pixels
              taller and visibly heavier, which reads as a different KIND of
              button when a rep moves between the three pages in one section. */}
          <button onClick={() => setInviteOpen(true)}
            style={{ padding: '9px 18px', background: '#3a6ad9', color: '#fff', border: 'none',
                     borderRadius: '9px', cursor: 'pointer', fontWeight: 600, fontSize: '13px',
                     whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            + Invite Account
          </button>
        </div>

        {/* ── KPI row ───────────────────────────────────────────────────── */}
        {!loading && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
            <StatCard label="Accounts" value={kpis.accounts} bg="#eff4ff" color="#3a6ad9"
              icon={<Icon name="building" size={18} color="#3a6ad9" />}
              onClick={() => { setPill('all'); setAttentionOpen(false); }}>
              <KpiDelta value={kpis.accounts} prev={kpis.accountsPrev} />
            </StatCard>

            {/* ⚠️ "Partners" MEANS ACTIVE MEMBERS AND THE CARD SAYS SO. partner_count
                counts only onboarding_status='active', so this will not reconcile
                against a headcount that includes people still in training — the
                sublabel is what stops the number being read as everybody. */}
            <StatCard label="Partners" value={kpis.partners} bg="#f0fde4" color="#5b8c00"
              icon={<Icon name="users" size={18} color="#5b8c00" />}>
              <KpiDelta value={kpis.partners} prev={kpis.partnersPrev} />
              {/* 🚨 THE SUBLABEL WAS "Active members only" AND IT DESCRIBED THE
                  OLD SUM. This card no longer sums member_active_count across
                  accounts — it counts partners with the Partners page's own
                  predicate — so the two now read the SAME number, and a caption
                  explaining why they differ would be explaining a gap that has
                  been closed. */}
              <div style={{ marginTop: 2, fontSize: 10, color: '#9ca3af' }}>Same count as the Partners page</div>
            </StatCard>

            <StatCard label="Files" value={kpis.referrals} bg="#eff6ff" color="#3a6ad9"
              icon={<Icon name="send" size={18} color="#3a6ad9" />}>
              <KpiDelta value={kpis.referrals} prev={kpis.referralsPrev} />
            </StatCard>

            {/* 🚨 NO "vs last month" ON THIS ONE, DELIBERATELY. Whether an account
                could be paid last month is recorded nowhere: banking, payee contact
                and license verification are current state with no history. A delta
                here would be a fabricated trend wearing the same styling as three
                measured ones, which is worse than an absent figure. */}
            <StatCard label="Needs Attention" value={kpis.blocked} bg="#fff0f3" color="#ff082f"
              icon={<Icon name="warning" size={18} color="#ff082f" />}
              onClick={() => setAttentionOpen((v) => !v)}>
              <KpiDelta note={attentionOpen ? 'Hide blocked accounts' : 'Show why — click to expand'} />
            </StatCard>
          </div>
        )}

        {/* ── Needs Attention, expanded ─────────────────────────────────── */}
        {!loading && attentionOpen && (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, marginBottom: 20, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>Accounts that cannot be paid</div>
              <button onClick={() => { setPill('cannot_be_paid'); setAttentionOpen(false); }}
                style={{ background: 'none', border: 'none', color: '#3a6ad9', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                Filter the table to these →
              </button>
            </div>
            {kpis.blockedRows.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                Nothing is blocked. Every account has banking, a payee contact and a verified entity license.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                {/* ⚠️ minWidth, NOT width ALONE. Three columns and the last one
                    holds a row of reason pills; at width:100% on a narrow screen
                    they crush into an unreadable stack instead of scrolling. Same
                    rule as the accounts table below. */}
                <table style={{ width: '100%', minWidth: 560, borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                      {['Account', 'Owner', 'Why it is blocked'].map((c) => (
                        <th key={c} style={{ padding: '9px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {kpis.blockedRows.map((a, i) => (
                      <tr key={a.id} onClick={() => openAccount(a)}
                        style={{ borderBottom: i < kpis.blockedRows.length - 1 ? '1px solid #f3f4f6' : 'none', cursor: 'pointer' }}>
                        <td style={{ padding: '11px 16px', fontWeight: 600, color: '#111827' }}>{a.company_name}</td>
                        <td style={{ padding: '11px 16px' }}>
                          <OwnerCell name={a.owner_name} hasRow={(a.assigned_row_count || 0) > 0}
                            extraCount={Math.max(0, (a.owner_count || 0) - 1)} />
                        </td>
                        <td style={{ padding: '11px 16px' }}>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {/* ⚠️ EVERY REASON, NOT THE FIRST. An account missing
                                banking AND a payee is two pieces of work; showing
                                one sends a rep back a second time. */}
                            {blockReasons(a).map((r) => (
                              <span key={r} style={{ padding: '2px 9px', borderRadius: 9999, background: '#fff0f3', color: '#ff082f', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>{r}</span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Filter bar — the Partners page shape ──────────────────────── */}
        {!loading && accounts.length > 0 && (
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '14px 16px', marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Search accounts…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ height: '36px', padding: '0 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none', width: '200px' }}
            />
            {/* 🚨 THE PILLS ARE GONE, BUT THE STATE THEY DROVE IS NOT — the KPI
                cards still set it. Removing both would have left "Filter the table
                to these" on the Needs Attention card silently doing nothing. This
                chip is the only way OUT of a KPI-driven filter now that no pill
                shows it is on, so it renders whenever one is active. */}
            {pill !== 'all' && (
              <button onClick={() => setPill('all')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 9999, fontSize: 12, fontWeight: 600, border: '1px solid #3a6ad9', background: '#3a6ad9', color: '#fff', cursor: 'pointer' }}>
                {pill === 'cannot_be_paid' ? 'Cannot be paid' : 'Unclaimed'} ✕
              </button>
            )}
            {/* ⚠️ EVERY DROPDOWN IS LABELLED — the Partners page learned this the
                hard way: two bare selects side by side, and the only way to tell
                which was which was to open one. */}
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
              Owner
              <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)}
                style={{ height: '36px', padding: '0 10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none', color: '#374151', backgroundColor: '#fff', cursor: 'pointer' }}>
                <option value="All">All</option>
                {owners.map((o) => <option key={o.id} value={o.name}>{o.name}</option>)}
                {/* ⚠️ THE TWO UNOWNED STATES SIT BELOW THE NAMES, not in place of
                    them. They are answers to the same question and must stay
                    distinguishable from each other — see components/OwnerCell.jsx. */}
                <option value="no_owner">No owner</option>
                <option value="no_record">Not on record</option>
              </select>
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
              State
              <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}
                style={{ height: '36px', padding: '0 10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none', color: '#374151', backgroundColor: '#fff', cursor: 'pointer' }}>
                <option value="All">All</option>
                {stateOptions.map((st) => <option key={st} value={st}>{st}</option>)}
              </select>
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
              Type
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                style={{ height: '36px', padding: '0 10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none', color: '#374151', backgroundColor: '#fff', cursor: 'pointer' }}>
                <option value="All">All</option>
                <option value="Real Estate Brokerage">Real Estate Brokerage</option>
                <option value="Mortgage Broker">Mortgage Broker</option>
                <option value="Homebuilder">Homebuilder</option>
              </select>
            </label>
            <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 'auto' }}>{visible.length} of {accounts.length}</span>
          </div>
        )}

        {/* 🔑 THE SAME FORM THE PARTNERS PAGE USES, STARTING ON THE ACCOUNT CARD.
            It was a separate thinner component — no batch, no toggle, its own
            required-field list — in front of the identical endpoint. Then it
            became this component with the toggle HIDDEN, which was still two
            different forms: the card pair is the clearest part of the form, and
            hiding it left this page unable to say what the two invitations are
            for. defaultKind is the only difference between the pages now. */}
        <PartnerInviteModal
          token={token}
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          onSent={loadAccounts}
          defaultKind="account"
        />

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#9ca3af' }}>Loading accounts...</div>
        ) : accounts.length === 0 ? (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', padding: '60px', textAlign: 'center', color: '#9ca3af' }}>
            <div style={{ marginBottom: '12px' }}><svg width="40" height="40" fill="none" stroke="#d1d5db" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg></div>
            <div style={{ fontWeight: '600', marginBottom: '4px' }}>No accounts yet</div>
            <div style={{ fontSize: '13px' }}>Accounts are created automatically when partners register with a company name</div>
          </div>
        ) : (
          /* ⚠️ SAME CLIPPING BUG AS THE PARTNERS TABLE, AND THE OWNER COLUMN
             ADDED A NINTH COLUMN TO IT. overflow:'hidden' cuts the right-hand
             columns off with no scrollbar — content that renders correctly and
             cannot be reached. See AdminREPartners for the longer note. */
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', minWidth: 980, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  {/* ⚠️ THE LAST COLUMN IS THE CHEVRON AND HAS NO KEY — a sort
                      arrow on a column with no data would do nothing. */}
                  {[['Company', 'company'], ['Type', 'type'], ['Partners', 'partners'],
                    ['Owner', 'owner'], ['State', 'state'], ['Files', 'referrals'],
                    ['Closed Volume', 'volume'], ['Created', 'created'], ['', null]].map(([h, key]) => (
                    <th key={h || 'chevron'} onClick={key ? () => toggleSort(key) : undefined}
                      style={{ padding: '11px 16px', textAlign: 'left', fontSize: '11px', color: sortKey === key ? '#3a6ad9' : '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em', cursor: key ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}>
                      {h}
                      {key && (
                        <span style={{ marginLeft: 4, color: sortKey === key ? '#3a6ad9' : '#d1d5db' }}>
                          {sortKey === key ? (sortDir === 'desc' ? '▼' : '▲') : '⇅'}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* 🚨 `visible`, NOT `accounts`. Rendering the unfiltered array
                    here would leave the filter and the KPI click both inert while
                    the count above them changed — the failure that looks like the
                    filter is broken rather than unwired. */}
                {visible.map(acct => (
                  <tr key={acct.id} onClick={() => openAccount(acct)}
                    style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>{acct.company_name}</div>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '8px',
                        background: acct.account_type === 'homebuilder' ? '#fff7ed' : '#eff6ff',
                        color: acct.account_type === 'homebuilder' ? '#7a5200' : '#2854c0' }}>
                        {ACCOUNT_KIND(acct)}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '15px', fontWeight: '800', color: '#111827' }}>{acct.partner_count}</td>
                    {/* ⚠️ AN ACCOUNT CAN HAVE SEVERAL OWNERS — one per partner on
                        it. owner_count drives the "+n", so a shared account reads
                        as shared rather than as belonging to whoever the query
                        happened to name first. hasRow is derived from the count:
                        zero distinct staff ids across every partner means nobody
                        on this account has an owner on record. */}
                    <td style={{ padding: '14px 16px', fontSize: '13px' }}>
                      <OwnerCell name={acct.owner_name} hasRow={(acct.assigned_row_count || 0) > 0}
                        extraCount={Math.max(0, (acct.owner_count || 0) - 1)} />
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: '#374151', fontWeight: '600' }}>{acct.states || '—'}</td>
                    <td data-testid="account-file-count" title={acct.file_count == null ? 'Could not be counted' : `${acct.submission_count || 0} submission(s), ${acct.referral_count || 0} referral(s)`}
                      style={{ padding: '14px 16px', fontSize: '14px', fontWeight: '700', color: acct.file_count > 0 ? '#3a6ad9' : '#9ca3af' }}>{acct.file_count == null ? '—' : acct.file_count}</td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '600', color: acct.closed_volume > 0 ? '#166534' : '#9ca3af' }}>
                      {acct.closed_volume > 0 ? '$' + (acct.closed_volume >= 1e6 ? (acct.closed_volume / 1e6).toFixed(1) + 'M' : acct.closed_volume >= 1e3 ? Math.round(acct.closed_volume / 1e3) + 'K' : Math.round(acct.closed_volume).toLocaleString()) : '—'}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '12px', color: '#9ca3af' }}>
                      {new Date(acct.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <button onClick={e => { e.stopPropagation(); openAccount(acct); }}
                        style={{ padding: '4px 10px', background: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', color: '#374151', fontWeight: '600' }}>
                        View →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 🔑 THE SHARED PANEL — was a 640px CENTRE modal, which on a 390px
            phone is a card with 20px of gutter and a sticky header inside a
            scrolling box. components/RightDrawer is a right drawer that is full width
            below its breakpoint, with the header outside the scroll. */}
        <RightDrawer
          open={!!selected}
          onClose={() => { setSelected(null); setPartners([]); }}
          title={selected ? selected.company_name : ''}
          subtitle={selected ? `${ACCOUNT_KIND(selected)} · ${partners.length} partner${partners.length !== 1 ? 's' : ''}` : ''}
          width={640}
        >
          {selected && (<>

                {/* ── Firm details ─────────────────────────────────────────
                    ⚠️ WHAT IS SHOWN OF THE BANKING IS DELIBERATELY NARROW. The
                    endpoint no longer returns the routing or account number at
                    all — it used to SELECT * and ship both to the browser — so
                    this can only render whether banking exists and which bank it
                    sits at. Enough to confirm the right account, not enough to
                    use it. */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Firm details</div>
                  {!editingFirm && (
                    <button onClick={() => { setEditingFirm(true); setFirmError(null); }}
                      style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 7, padding: '4px 10px', fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer' }}>Edit</button>
                  )}
                </div>

                {editingFirm ? (
                  <div style={{ display: 'grid', gap: 12, marginBottom: 22, padding: '14px 16px', border: '1px solid #e5e7eb', borderRadius: 10, background: '#fafafa' }}>
                    <div>
                      <div style={fieldLabel}>Company name</div>
                      <input style={fieldInput} value={firmForm.company_name}
                        onChange={(e) => setFirmForm((p) => ({ ...p, company_name: e.target.value }))} />
                      {/* ⚠️ RENAMING MOVES THE MATCHING KEY TOO. The server
                          recomputes company_name_normalized, which is what new
                          partners are matched on — a display-only rename would
                          leave them landing in a firm whose name no longer
                          exists, or creating a duplicate beside it. */}
                      <div style={fieldHint}>New partners are matched to this firm by name, so a rename moves them with it.</div>
                    </div>
                    <div>
                      <div style={fieldLabel}>Entity license</div>
                      <input style={fieldInput} value={firmForm.entity_license_number}
                        onChange={(e) => setFirmForm((p) => ({ ...p, entity_license_number: e.target.value }))} />
                      {/* 🚨 A TYPED LICENSE IS NOT A CHECKED ONE, and the badge
                          above will say so after saving. */}
                      <div style={fieldHint}>Editing this clears the verification stamp — it becomes a claimed number again.</div>
                    </div>
                    <div>
                      {/* 🚨 THIS IS WHO CONTROLS WHERE THE FIRM'S MONEY GOES. The
                          refusals partners see already promise Doorly can
                          re-appoint it; until now no route could. Restricted to
                          active members of this firm — naming an outsider would
                          put a stranger in charge of the payout destination. */}
                      <div style={fieldLabel}>Payee contact — manages payout details</div>
                      <select style={fieldInput} value={firmForm.payee_contact_partner_id}
                        onChange={(e) => setFirmForm((p) => ({ ...p, payee_contact_partner_id: e.target.value }))}>
                        <option value="">Not set</option>
                        {partners.filter((p) => !p.archived_at).map((p) => (
                          <option key={p.id} value={p.id}>
                            {`${p.first_name} ${p.last_name}`}{p.account_role === 'broker' ? ' (broker of record)' : ''}
                          </option>
                        ))}
                      </select>
                      <div style={fieldHint}>Changing this changes who can edit the firm's banking.</div>
                    </div>
                    <div>
                      <div style={fieldLabel}>Internal note</div>
                      <textarea style={{ ...fieldInput, minHeight: 60, resize: 'vertical' }} value={firmForm.account_note}
                        onChange={(e) => setFirmForm((p) => ({ ...p, account_note: e.target.value }))} />
                    </div>
                    {firmError && <div style={{ fontSize: 12.5, color: '#ff082f', fontWeight: 600 }}>{firmError}</div>}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={saveFirm} disabled={firmSaving}
                        style={{ padding: '8px 16px', background: firmSaving ? '#e5e7eb' : '#3a6ad9', color: firmSaving ? '#9ca3af' : '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: firmSaving ? 'not-allowed' : 'pointer' }}>
                        {firmSaving ? 'Saving…' : 'Save changes'}
                      </button>
                      <button onClick={() => { setEditingFirm(false); setFirmError(null); }}
                        style={{ padding: '8px 12px', background: 'none', border: 'none', color: '#6b7280', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '10px 18px', marginBottom: 22, fontSize: 13 }}>
                  {/* 🚨 THE PANEL HAD NO PERSON ON IT. Company, type, license,
                      banking and a bare payee name — and no way to contact anyone
                      at the firm without opening the member list and guessing who
                      is in charge. The broker of record is that person. */}
                  <Detail label="Broker / contact"
                    value={detail.broker ? detail.broker.name : 'No broker of record yet'}
                    badge={detail.broker ? (detail.broker.archived ? 'archived' : null) : 'none on record'} />
                  <Detail label="Contact email" value={detail.broker?.email || '—'} />
                  <Detail label="Contact phone" value={detail.broker?.phone || '—'} />
                  {/* ⚠️ THE OWNER IS READ, NEVER ASSIGNED BY OPENING THIS PANEL.
                      A screen that picked a rep as a side effect of being looked
                      at would attribute firms to whoever happened to open them. */}
                  <Detail label="Partner Success owner"
                    value={detail.owner?.name || 'None on record'}
                    badge={detail.owner?.staffUserId ? null : 'needs assigning'} />
                  <Detail label="Type" value={ACCOUNT_KIND(selected)} />
                  <Detail label="States" value={selected.states || '—'} />
                  {/* 🚨 THE RECORDED CHECK, NOT entity_license_number (Jesse, 2026-11-02). Total Lending's firm NMLS was
                      checked and recorded Approved, and this said "None on file" because it read the TREC-registry field
                      nothing records a check into. firm_licence is what the licence gate reads (utils/firmLicence). */}
                  {(() => {
                    // The list row carries the same firm_licence, so the field is right before the detail read lands.
                    const fl = firmLicenceText(detail.firmLicence || selected.firm_licence);
                    return <Detail label="Entity license" testid="account-firm-licence" value={fl.value} badge={fl.badge} />;
                  })()}
                  <Detail label="Banking"
                    value={selected.ach_completed
                      ? `${selected.ach_bank_name || 'On file'}${selected.ach_account_holder_name ? ' · ' + selected.ach_account_holder_name : ''}`
                      : 'None on file'}
                    badge={selected.ach_completed ? null : 'cannot be paid'} />
                  {/* 🚨 "WHO RUNS THE FIRM" AND "WHO CONTROLS THE MONEY" ARE
                      DIFFERENT QUESTIONS, and nothing on screen distinguished
                      them. The payee contact used to be whoever supplied banking
                      FIRST — not appointed, just first — so at every production
                      firm with banking it is not the broker. It now defaults to
                      the broker of record when a firm is created, and the badge
                      says when it is somebody else. */}
                  <Detail label="Payee contact"
                    value={detail.payee ? detail.payee.name : 'Not set'}
                    badge={detail.payee ? (detail.payee.is_broker ? null : 'not the broker') : 'cannot be changed by anyone'} />
                  <Detail label="Created" value={selected.created_at ? new Date(selected.created_at).toLocaleDateString() : '—'} />
                </div>
                )}

                {/* ⚠️ THE ACCOUNT NOTE IS SHOWN WHEN THERE IS ONE. It exists to
                    explain a firm whose name or state is not self-evident — see
                    account 11 — and a note nobody renders is a note nobody reads. */}
                {selected.account_note && (
                  <div style={{ marginBottom: 22, padding: '12px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, fontSize: 12.5, color: '#713f12', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                    {selected.account_note}
                  </div>
                )}

                {/* ── Documents ────────────────────────────────────────────
                    🔑 THE W-9 IS THE FIRM'S. Doorly pays the firm, so the W-9
                    identifies the entity receiving the money — asking an agent
                    whose fee goes to their brokerage asked the wrong party. It
                    sits here with the agreements rather than on each partner. */}
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>Documents</div>
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 22, overflow: 'hidden' }}>
                  <div style={{ padding: '10px 14px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: '#111827', fontWeight: 600 }}>Firm W-9</span>
                    <span style={{ color: selected.doc_w9 ? '#2d6a0a' : '#9ca3af' }}>
                      {selected.doc_w9
                        ? `On file${selected.doc_w9_uploaded_at ? ' · ' + new Date(selected.doc_w9_uploaded_at).toLocaleDateString() : ''}`
                        : 'Not uploaded'}
                    </span>
                  </div>
                  {partners.length === 0 ? (
                    <div style={{ padding: '12px 14px', fontSize: 13, color: '#9ca3af' }}>No partners at this account yet.</div>
                  ) : partners.map((p) => (
                    <div key={`doc-${p.id}`} style={{ padding: '10px 14px', borderBottom: '1px solid #f3f4f6', fontSize: 13, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                      <span style={{ color: '#111827' }}>
                        {p.first_name} {p.last_name}
                        {p.account_role === 'broker' && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#3a6ad9' }}>BROKER</span>}
                      </span>
                      <span style={{ color: '#6b7280', textAlign: 'right' }}>
                        {/* ⚠️ THREE STATES, NOT TWO. An agreement the agent signed
                            and nobody counter-signed is NOT complete, and showing
                            it as signed is the one error this section could make. */}
                        {/* 🔑 A FOURTH STATE (hotfix 11-01): the broker of record's
                            own agreement is signed in the firm's capacity, so
                            nothing of hers awaits a broker. */}
                        {!Number(p.agreement_signed)
                          ? <span style={{ color: '#9ca3af' }}>Agreement not signed</span>
                          : p.account_role === 'broker'
                            ? <span style={{ color: '#2d6a0a' }}>Agreement signed as broker of record</span>
                          : p.countersigned_at
                            ? <span style={{ color: '#2d6a0a' }}>Agreement signed &amp; counter-signed</span>
                            : <span style={{ color: '#b45309' }}>Awaiting broker counter-signature</span>}
                        {p.agreement_version && <span style={{ color: '#9ca3af' }}> · v{p.agreement_version}</span>}
                        {(p.doc_real_estate_license || p.doc_mlo_license)
                          ? <span style={{ color: '#9ca3af' }}> · license on file</span>
                          : <span style={{ color: '#9ca3af' }}> · no license doc</span>}
                      </span>
                    </div>
                  ))}
                </div>

                <div style={{ fontSize: '12px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '12px' }}>Partners</div>
                {loadingDetail ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>Loading...</div>
                ) : partners.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>No partners linked</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {partners.map(p => (
                      <div key={p.id} onClick={() => setSelectedPartner(p)}
                        style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '14px', cursor: 'pointer', transition: 'background 0.1s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                        onMouseLeave={e => e.currentTarget.style.background = '#f9fafb'}>
                        <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', fontWeight: '800', color: '#3a6ad9', flexShrink: 0 }}>
                          {(p.first_name?.[0] || '?').toUpperCase()}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>{p.first_name} {p.last_name}</div>
                          <div style={{ fontSize: '12px', color: '#9ca3af' }}>{p.email}</div>
                          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                            {p.license_state && `${p.license_state} · `}{p.partner_type || <span style={{ color: '#b45309', fontWeight: 700 }}>Type not set</span>}
                          </div>
                        </div>
                        <span style={{ fontSize: '11px', fontWeight: '700', padding: '3px 10px', borderRadius: '10px', flexShrink: 0,
                          background: p.display_status === 'active' ? '#f0fdf4' : '#fff7ed',
                          color: p.display_status === 'active' ? '#166534' : '#7a5200',
                          border: `1px solid ${p.display_status === 'active' ? '#bbf7d0' : '#fed7aa'}` }}>
                          {p.display_status === 'active' ? '● Active' : '○ Pending'}
                        </span>
                        {p.file_count > 0 && (
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ fontSize: '14px', fontWeight: '800', color: '#3a6ad9' }}>{p.file_count}</div>
                            <div style={{ fontSize: '10px', color: '#9ca3af' }}>{(p.files_label || 'Files').toLowerCase()}</div>
                          </div>
                        )}
                        <span style={{ color: '#d1d5db', fontSize: '16px', flexShrink: 0 }}>›</span>
                      </div>
                    ))}
                  </div>
                )}

              {/* ⚠️ THE REFERRALS, NOT JUST THE COUNT. The partner rows above show
                  a referral number that used to lead nowhere — you could see that
                  an account had sent 7 and had no way to learn who they were or
                  what happened to them. This is the list behind that number. */}
              <div style={{ padding: '4px 24px 24px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '12px' }}>
                  <div data-testid="account-files-heading" style={{ fontSize: '12px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{detail.filesLabel || 'Files'}</div>
                  {referrals.length > 0 && (
                    <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                      {referrals.filter(r => !r.is_archived && !r.is_adversed).length} active of {referrals.length}
                    </div>
                  )}
                </div>
                {loadingDetail ? (
                  <div style={{ textAlign: 'center', padding: '24px', color: '#9ca3af' }}>Loading...</div>
                ) : referrals.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px', color: '#9ca3af', fontSize: '13px' }}>No {(detail.filesLabel || 'Files').toLowerCase()} from this account yet</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {referrals.map(r => {
                      // ⚠️ THE FLAG OUTRANKS THE STATUS, same rule as the referrals
                      // page. An archived borrower keeps whatever status they had
                      // when they were archived, so showing the raw status here
                      // would make this list disagree with that one — which is the
                      // disagreement that started all of this.
                      const st = r.is_adversed ? { label: 'Adversed', color: '#ff082f', bg: '#fff0f3' }
                        : r.is_archived ? { label: 'Archived', color: '#9ca3af', bg: '#f3f4f6' }
                        : (REFERRAL_STATUS[r.application_status] || { label: r.application_status || '—', color: '#6b7280', bg: '#f3f4f6' });
                      const dim = r.is_archived || r.is_adversed;
                      return (
                        // 🔑 THE SERVER'S LINK (utils/partnerFileCounts.staffLinkFor): the borrower file once they have
                        // registered, the submission view while a submission waits on them, nothing for an unregistered
                        // referral. Never /admin/borrowers/<a submission id>.
                        <div key={`${r.source}-${r.id}`} data-testid="account-file-row" data-kind={r.kind}
                          onClick={() => { if (r.link) navigate(r.link); }}
                          style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '10px 14px',
                            display: 'flex', alignItems: 'center', gap: '12px', opacity: dim ? 0.6 : 1,
                            cursor: r.link ? 'pointer' : 'default' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '13px', fontWeight: '700', color: '#111827' }}>
                              {`${r.first_name || ''} ${r.last_name || ''}`.trim() || r.email || '—'}
                            </div>
                            <div style={{ fontSize: '11px', color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {r.partner_name ? `via ${r.partner_name}` : ''}{r.buying_state ? ` · ${r.buying_state}` : ''}
                              {/* A referral that has not registered yet has no borrower record to open. */}
                              {r.source === 'referral' ? ' · not registered yet' : ''}
                              {r.kind === 'submission' && !r.borrower_user_id ? ' · submission · awaiting borrower registration' : ''}
                            </div>
                          </div>
                          <span style={{ fontSize: '11px', fontWeight: '700', padding: '3px 10px', borderRadius: '10px',
                            flexShrink: 0, background: st.bg, color: st.color }}>{st.label}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
          </>)}
        </RightDrawer>
        {/* 🔑 THE SAME PANEL AGAIN. This was a THIRD width (560px) on the same
            page as the 640px one above it — two row-detail treatments inside one
            screen, which is how a section ends up with four. */}
        <RightDrawer
          open={!!selectedPartner}
          onClose={() => { setSelectedPartner(null); setPartnerTab('details'); }}
          title={selectedPartner ? `${selectedPartner.first_name} ${selectedPartner.last_name}` : ''}
          subtitle={selectedPartner ? selectedPartner.email : ''}
        >
          {selectedPartner && (<>

              <div style={{ display: 'flex', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
                {['details', 'messages'].map(tab => (
                  <button key={tab} onClick={() => {
                    setPartnerTab(tab);
                    if (tab === 'messages' && partnerMessages.length === 0) {
                      setMsgLoading(true);
                      axios.get(`${API}/api/admin/partners/${selectedPartner.id}/messages`, { headers })
                        .then(r => { setPartnerMessages(r.data.messages || []); setPartnerConvId(r.data.conversation?.id || null); })
                        .catch(() => {})
                        .finally(() => setMsgLoading(false));
                    }
                  }} style={{ flex: 1, padding: '10px', background: 'none', border: 'none', borderBottom: partnerTab === tab ? '2px solid #3a6ad9' : '2px solid transparent', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: partnerTab === tab ? '#3a6ad9' : '#9ca3af', textTransform: 'capitalize' }}>
                    {tab}
                  </button>
                ))}
              </div>

              <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
                {partnerTab === 'details' && (
                  <>
                    <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
                      {[
                        { label: 'Phone', value: selectedPartner.phone || '—' },
                        { label: 'Type', value: selectedPartner.partner_type || 'Type not set — assign before agreement or payout' },
                        { label: 'License State', value: selectedPartner.license_state || '—' },
                        { label: 'License #', value: selectedPartner.license_number || '—' },
                        { label: 'Status', value: selectedPartner.display_status === 'active' ? '● Active' : '○ Pending' },
                        { label: selectedPartner.files_label || 'Files', value: selectedPartner.file_count == null ? '—' : String(selectedPartner.file_count) },
                        { label: 'Closed', value: String(selectedPartner.closed_count || 0) },
                        { label: 'Joined', value: selectedPartner.created_at ? new Date(selectedPartner.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—' },
                      ].map(f => (
                        <div key={f.label} style={{ background: '#f9fafb', borderRadius: '8px', padding: '10px 12px' }}>
                          <div style={{ fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '3px', fontWeight: '600' }}>{f.label}</div>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>{f.value}</div>
                        </div>
                      ))}
                    </div>
                    <button onClick={() => window.open(`mailto:${selectedPartner.email}`, '_blank')}
                      style={{ padding: '8px 16px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>
                      Email Partner
                    </button>
                  </>
                )}

                {partnerTab === 'messages' && (
                  <>
                    {msgLoading ? (
                      <div style={{ textAlign: 'center', padding: '32px', color: '#9ca3af', fontSize: 13 }}>Loading messages...</div>
                    ) : partnerMessages.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '32px', color: '#9ca3af', fontSize: 13 }}>No messages yet. Send the first message below.</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                        {partnerMessages.map((m, i) => {
                          // 'eva' folded in defensively — see AdminREPartners.js.
                          // Partner threads are gated out of triage v1; this keeps
                          // the two surfaces from diverging if that ever changes.
                          const isAdmin = ['support', 'admin', 'eva'].includes(m.sender_role);
                          return (
                            <div key={m.id || i} style={{ display: 'flex', justifyContent: isAdmin ? 'flex-end' : 'flex-start' }}>
                              <div style={{ maxWidth: '80%', padding: '10px 13px', borderRadius: isAdmin ? '14px 14px 4px 14px' : '14px 14px 14px 4px', background: isAdmin ? '#3a6ad9' : '#f3f4f6', color: isAdmin ? '#fff' : '#111827', fontSize: 13, lineHeight: '1.5' }}>
                                {/* 🔑 fp-format: a formatted staff message renders from the server's HTML (utils/messageRichText); the rest is
                                    the plain text, as always. A staff attachment opens through the signed attachment route. */}
                                {hasRichBody(m)
                                  ? <RichMessageBody msg={m} dark={isAdmin} />
                                  : <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.body}</div>}
                                {m.has_attachment && m.attachment_name && (
                                  <div style={{ marginTop: 6 }}><MessageAttachment msgId={m.id} name={m.attachment_name} token={token} dark={isAdmin} /></div>
                                )}
                                <div style={{ fontSize: 10, marginTop: 4, opacity: 0.6 }}>
                                  {new Date(m.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        <div ref={msgEndRef} />
                      </div>
                    )}
                    <div style={{ marginTop: 8 }}>
                      <StaffComposer key={selectedPartner.id} token={token} onSend={sendPartnerReply} testId="partner-pane-composer"
                        placeholder="Type a message… (⌘↵ to send)" sendLabel="Send" />
                    </div>
                  </>
                )}
              </div>
          </>)}
        </RightDrawer>
      </div>
    </AdminLayout>
  );
}
